import classNames from 'classnames'
import type { FC } from 'react'
import React from 'react'

import TableBody from './table-body'
import TableCaption from './table-caption'
import TableCell from './table-cell'
import { TableContextProvider, useTableContext } from './table-context'
import TableFoot from './table-foot'
import TableHead from './table-head'
import TableHeadCell from './table-head-cell'
import TableRow from './table-row'
import type { TableComponents, TableProps } from './types'
import type { Theme } from '../../theme-provider/types'
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui'
import { get } from '../../utils/object'
import { getBorder } from '../../utils/props-utils'
import { PanePrivate } from '../_layouts/pane/pane'
import { BoxPrivate } from '../_primitives/box/box'

const defaultStyles = {
  borderCollapse: 'collapse',
  borderSpacing: 0,
  textAlign: 'left',
  width: '100%'
}

export const Table: FC<TableProps & { theme?: Theme }> & TableComponents = ({
  useContext: useContextProp,
  a11yTitle,
  children,
  variant,
  className,
  ...props
}) => {
  const theme = useSafeThemeUI(props)
  const { border, ...borderProps } = getBorder(props)
  // When integrating with an 3rd Party we can pass variant through a TableContext
  const { variant: tableVariant } = useTableContext()
  const _variant = tableVariant || variant
  const _useContext = !!tableVariant || useContextProp
  const wrapperTheme = get(
    theme,
    _useContext && _variant ? `tables.${_variant}.wrapper` : `tables.wrapper`
  )
  const useStyledBorderProps =
    border === 'shadow' || typeof border === 'boolean'
  // eslint-disable-next-line no-console
  // console.log('rendering table.table')

  const mainContent = (
    <BoxPrivate
      as="table"
      className={classNames('csg-tui-table', className)}
      aria-label={a11yTitle}
      __themeKey="tables"
      variant={
        _useContext && _variant
          ? `${_variant}.table`
          : _variant
            ? _variant
            : 'table'
      }
      {...props}
      __css={defaultStyles}
    >
      {children}
    </BoxPrivate>
  )

  const wrapContext =
    !tableVariant && !!_useContext ? (
      <TableContextProvider variant={_variant}>
        {mainContent}
      </TableContextProvider>
    ) : (
      mainContent
    )

  return border ||
    Object.keys(borderProps).length > 0 ||
    Object.keys(getBorder(wrapperTheme)).length > 0 ? (
    <PanePrivate
      __themeKey="tables"
      variant={
        _useContext && _variant
          ? `${_variant}.wrapper`
          : _variant
            ? _variant
            : 'wrapper'
      }
      styledBorder={useStyledBorderProps ? border : undefined}
      {...{
        border: useStyledBorderProps ? undefined : border,
        ...borderProps
      }}
    >
      {wrapContext}
    </PanePrivate>
  ) : (
    wrapContext
  )
}

Table.Body = TableBody
Table.Caption = TableCaption
Table.Cell = TableCell
Table.Foot = TableFoot
Table.Head = TableHead
Table.HeadCell = TableHeadCell
Table.Row = TableRow

Table.defaultProps = {
  useContext: true
}

export default Table
